import React, { useState } from 'react';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import Heading from 'components/Heading';
import { FirmSelector } from 'components/FirmSelector';
import Button from 'components/Button';
import FieldRow from 'components/FormElements/FieldRow';
import { AppDatePicker } from 'components/FormElements';
import ButtonGroup from 'components/ButtonGroup';
import Spacing from 'components/Spacing';
import trpcApi, { ApiResponses } from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import { CalendarDay, numberToDisplayString } from 'further-ui/utils';

type UpdateInvestmentBalanceCachesResponse =
  ApiResponses['dataTools']['updateInvestmentBalanceCaches'];
type RerunDailyInvestmentReportResponse =
  ApiResponses['dataTools']['rerunDailyInvestmentReport'];

const DataTools: React.FC = () => {
  const [firmIdForBalances, setFirmIdForBalances] = useState<string>('');
  const [firmIdForDailyReport, setFirmIdForDailyReport] = useState<string>('');
  const [dailyReportRunDate, setDailyReportRunDate] = useState<Date | null>(
    null,
  );
  const [lastDailyReportResult, setLastDailyReportResult] =
    useState<RerunDailyInvestmentReportResponse | null>(null);
  const notification = useNotification();

  const updateInvestmentBalanceCaches =
    trpcApi.dataTools.updateInvestmentBalanceCaches.useMutation(
      useHandleTrpcMutation<UpdateInvestmentBalanceCachesResponse>()({
        onSuccess: (data) => {
          notification.success(
            `Cache updates for ${numberToDisplayString(
              data.count,
            )} investments added to the queue.`,
          );
        },
      }),
    );

  const handleUpdateBalances = () => {
    updateInvestmentBalanceCaches.mutate({
      firmId: firmIdForBalances || undefined,
    });
  };

  const rerunDailyInvestmentReport =
    trpcApi.dataTools.rerunDailyInvestmentReport.useMutation(
      useHandleTrpcMutation<RerunDailyInvestmentReportResponse>()({
        onSuccess: (data) => {
          setLastDailyReportResult(data);
          notification.success(
            `Daily investment report rerun: ${numberToDisplayString(
              data.fundsFound,
            )} funds found, ${numberToDisplayString(
              data.fundsProcessed,
            )} processed, ${numberToDisplayString(data.fundsSkipped)} skipped.`,
          );
        },
      }),
    );

  const handleRerunDailyInvestmentReport = () => {
    if (!dailyReportRunDate) {
      notification.error('Please select a run date.');
      return;
    }

    rerunDailyInvestmentReport.mutate({
      firmId: firmIdForDailyReport || undefined,
      runDate: new CalendarDay(dailyReportRunDate),
    });
  };

  return (
    <PageContainer heading="Data Tools">
      <PageContent withSpacing>
        <Heading variant="h2" noMargin>
          Update Investment Balance Caches
        </Heading>

        <Spacing>
          <FieldRow title="Firm" centerTitle>
            <FirmSelector
              firmId={firmIdForBalances}
              onChange={(id) => setFirmIdForBalances(id)}
              placeholder="Select a firm (optional)"
              allowDeselection
              deselectionLabel="All firms"
            />
          </FieldRow>

          <ButtonGroup>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdateBalances}
              loading={updateInvestmentBalanceCaches.isPending}
            >
              Update investment balance caches
            </Button>
          </ButtonGroup>
        </Spacing>

        <Heading variant="h2" noMargin>
          Rerun Daily Investment Report
        </Heading>

        <Spacing>
          <div>
            Warning: this will send real emails and may create duplicates. Use
            only when needed and check worker logs for the send outcome. If no
            firm is selected, all firms will be re-sent the report for the
            selected date (if there are investments to report)
          </div>

          <FieldRow title="Firm" centerTitle>
            <FirmSelector
              firmId={firmIdForDailyReport}
              onChange={(id) => setFirmIdForDailyReport(id)}
              placeholder="Select a firm (optional)"
              allowDeselection
              deselectionLabel="All firms"
            />
          </FieldRow>

          <FieldRow title="Run date" centerTitle>
            <AppDatePicker
              value={dailyReportRunDate}
              onChange={(date) => setDailyReportRunDate(date)}
              disableFuture
            />
          </FieldRow>

          <ButtonGroup>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRerunDailyInvestmentReport}
              loading={rerunDailyInvestmentReport.isPending}
            >
              Rerun daily investment report
            </Button>
          </ButtonGroup>

          {lastDailyReportResult ? (
            <div>
              Last run summary:{' '}
              {numberToDisplayString(lastDailyReportResult.fundsFound)} funds
              found,{' '}
              {numberToDisplayString(lastDailyReportResult.fundsProcessed)}{' '}
              processed,{' '}
              {numberToDisplayString(lastDailyReportResult.fundsSkipped)}{' '}
              skipped,{' '}
              {numberToDisplayString(lastDailyReportResult.sendAttempts)} send
              attempts,{' '}
              {numberToDisplayString(
                lastDailyReportResult.sendSkipsNoSafeRecipients,
              )}{' '}
              skipped (no safe recipients),{' '}
              {numberToDisplayString(lastDailyReportResult.sendFailures)}{' '}
              failures.
            </div>
          ) : null}
        </Spacing>
      </PageContent>
    </PageContainer>
  );
};

export default DataTools;
